import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Anchor, ActionAnchor } from './common/Anchor'
import {
  Spin,
  Table,
  Column,
  Menu,
  Dropdown,
  Icon,
  Modal,
  Badge,
  Tooltip,
} from './common/Ant'
import Divider from './common/Divider'
import Input from './common/Input'
import Select, { Option } from './common/Select'
import { H3 } from './common/Headers'
import { createQueryString } from '../utils/queryParams'
import { dateFormatter, dateTimeFormatter } from '../utils/date'
import { toTitleCase, renderReportType } from '../utils/textFormatters'
import { escapeRegExp, configurePagination } from '../utils/helpers'
import {
  REPORT_SEND_DAYS,
  DAYS_INTERVALS,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  VIEW_ALL,
  ALL,
  REPORT_TYPES,
} from '../constants'

const renderReportSendDay = x => {
  const text = Object.keys(REPORT_SEND_DAYS).filter(
    y => REPORT_SEND_DAYS[y] === x
  )

  return !!text.length ? toTitleCase(text[0]) : '--'
}

const renderDaysInterval = x => {
  const text = Object.keys(DAYS_INTERVALS).filter(y => DAYS_INTERVALS[y] === x)

  return !!text.length ? toTitleCase(text[0]) : '--'
}

const handleViewAll = fn => arr => fn(arr.indexOf(ALL) !== -1 ? [] : arr)

const AllReportConfigs = ({
  getAllReportConfigs,
  deleteReportConfig,
  setReportConfig,
  reportConfigs,
  sites,
  isLoading,
}) => {
  useEffect(() => {
    getAllReportConfigs(createQueryString({ perPage: 'all' }))
  }, [getAllReportConfigs])

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [reportName, setReportName] = useState('')
  const [upcomingSchedules, setUpcomingSchedules] = useState([])
  const [searchText, setSearchText] = useState('')
  const [siteSlug, setSiteSlug] = useState([])
  const [reportType, setReportType] = useState([])
  const [page, setPage] = useState(DEFAULT_PAGE)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  const filteredReportConfigs = useMemo(() => {
    const regex = new RegExp(escapeRegExp(searchText), 'i')

    return reportConfigs.filter(
      x =>
        (!siteSlug.length || siteSlug.includes(x.siteSlug)) &&
        (!reportType.length || reportType.includes(x.reportType)) &&
        (!searchText || [x.name, x.userName].some(y => !!y && regex.test(y)))
    )
  }, [reportConfigs, searchText, siteSlug, reportType])

  const handleModalOpen = reportConfig => {
    setReportName(reportConfig.name)
    setUpcomingSchedules(reportConfig.upcomingSchedules)
    setIsModalVisible(true)
  }

  const handleModalCancel = () => {
    setReportName('')
    setUpcomingSchedules([])
    setIsModalVisible(false)
  }

  return isLoading ? (
    <Spin size="large" className="w-100 center mv5" />
  ) : (
    <section className="mb5 AllReportConfigs">
      <div className="flex-l items-center-l">
        <Select
          className="w-third-l mr3-l"
          placeholder="Site"
          mode="multiple"
          input={{
            value: siteSlug,
            onChange: handleViewAll(x => {
              setSiteSlug(x)
              setPage(DEFAULT_PAGE)
            }),
          }}
          filterable
        >
          {[{ slug: ALL, name: VIEW_ALL }, ...sites].map(x => (
            <Option value={x.slug} key={x.slug}>
              {x.name}
            </Option>
          ))}
        </Select>
        <Select
          className="w-third-l mr3-l"
          placeholder="Report Type"
          mode="multiple"
          input={{
            value: reportType,
            onChange: handleViewAll(x => {
              setReportType(x)
              setPage(DEFAULT_PAGE)
            }),
          }}
          filterable
        >
          {[ALL, ...Object.keys(REPORT_TYPES)].map(x => (
            <Option value={x} key={x}>
              {x === ALL ? VIEW_ALL : renderReportType(REPORT_TYPES[x])}
            </Option>
          ))}
        </Select>
        <Input
          className="w-third-l"
          input={{
            name: 'report-config-search',
            placeholder: 'Search by name or creator',
            value: searchText,
            onChange: ev => {
              setSearchText(ev.currentTarget.value)
              setPage(DEFAULT_PAGE)
            },
          }}
        />
      </div>
      <Table
        dataSource={filteredReportConfigs}
        rowKey="slug"
        pagination={{
          ...configurePagination({
            page,
            perPage,
            total: filteredReportConfigs.length,
            onShowSizeChange: (page, perPage) => setPerPage(perPage),
          }),
          onChange: page => setPage(page),
        }}
        size="small"
        scroll={{ x: 815 }}
        tableLayout="auto"
      >
        <Column
          title="Config Name"
          dataIndex="name"
          render={(text, record) => (
            <span>
              <Tooltip title={record.active ? 'Active' : 'Inactive'}>
                <Badge status={record.active ? 'success' : 'default'} />
              </Tooltip>
              {text}
            </span>
          )}
        />
        <Column
          title="Site"
          dataIndex="siteName"
          render={(text, record) => (
            <Anchor to={`/sites/${record.siteSlug}`}>{text}</Anchor>
          )}
        />
        <Column
          title="Type"
          dataIndex="reportType"
          render={text => renderReportType(text)}
        />
        <Column title="Created By" dataIndex="userName" />
        <Column
          title="Last Sent"
          dataIndex="lastRunTime"
          render={text => (text ? dateFormatter(text) : '--')}
        />
        <Column
          title="Next Run Time"
          dataIndex="nextRunTime"
          render={(text, record) => {
            if (record.upcomingSchedules.length > 0) {
              return (
                <span>
                  {dateFormatter(text)} (
                  <ActionAnchor onClick={() => handleModalOpen(record)}>
                    View Upcoming Schedule
                  </ActionAnchor>
                  )
                </span>
              )
            } else {
              return '--'
            }
          }}
        />
        <Column
          title="Repeat Until"
          dataIndex="repeatUntil"
          render={text => dateFormatter(text)}
        />
        <Column
          title="Delivery Day"
          dataIndex="reportSendDay"
          render={text => renderReportSendDay(text)}
        />
        <Column
          title="Report Interval"
          dataIndex="daysInterval"
          render={text => renderDaysInterval(text)}
        />
        <Column
          width={100}
          render={(text, record) => {
            const menu = (
              <Menu>
                <Menu.Item key="edit-network">
                  <ActionAnchor onClick={() => setReportConfig(record)}>
                    Edit
                  </ActionAnchor>
                </Menu.Item>
                <Menu.Item key="delete-network">
                  <ActionAnchor onClick={() => deleteReportConfig(record.slug)}>
                    Delete
                  </ActionAnchor>
                </Menu.Item>
              </Menu>
            )

            return (
              <Dropdown overlay={menu} trigger={['click']}>
                <a className="ant-dropdown-link">
                  Actions <Icon type="down" />
                </a>
              </Dropdown>
            )
          }}
        />
      </Table>

      <Modal
        className="Modal"
        visible={isModalVisible}
        footer={null}
        onCancel={handleModalCancel}
        destroyOnClose
      >
        <div>
          <H3>Next Three Run Times for {reportName}</H3>
          {upcomingSchedules.map((x, i) => (
            <div>
              <div className="flex">
                <div className="b w-30-ns">Run Time:</div>
                <div>{dateTimeFormatter(x.runTime)}</div>
              </div>
              <div className="flex">
                <div className="b w-30-ns">Report Start Time:</div>
                <div>{dateTimeFormatter(x.startTime)}</div>
              </div>
              <div className="flex mb3">
                <div className="b w-30-ns">Report End Time:</div>
                <div>{dateTimeFormatter(x.endTime)}</div>
              </div>
              {upcomingSchedules.length > i + 1 && <Divider />}
            </div>
          ))}
        </div>
      </Modal>
    </section>
  )
}

AllReportConfigs.propTypes = {
  getAllReportConfigs: PropTypes.func.isRequired,
  deleteReportConfig: PropTypes.func.isRequired,
  setReportConfig: PropTypes.func.isRequired,
  reportConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
  sites: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default AllReportConfigs
